import { getHomeBreadcrumb } from "@atoms/breadcrumbs/Breadcrumbs.helpers";
import { Breadcrumb } from "@atoms/breadcrumbs/Breadcrumbs.types";
import { routes } from "@utils/routes/mapping";
import { HitData } from "components/pages/search/hit";
import { Hit, MultiSearchQueryWithFederation } from "meilisearch";

export type Blog = {
  id: number;
  title: string;
  slug: string;
  publicationTimestamp?: number;
  categorySlug?: string;
  content?: string;
};

export type CaseStudy = {
  id: number;
  title: string;
  slug: string;
  firstParagraph?: string;
  publicationTimestamp?: number;
  content?: string;
  industry?: string;
};

export type News = {
  id: number;
  title: string;
  slug: string;
  publicationTimestamp?: number;
  content?: string;
};

export type Page = {
  id: number;
  title: string;
  slug: string;
  strapiId:
    | "business-sector"
    | "page"
    | "capability"
    | "service"
    | "solution"
    | "expertise";
  excerpt?: string;
};

export type Author = {
  id: number;
  firstName: string;
  lastName: string;
  slug: string;
  hasRoute: boolean;
  description?: string;
};

export type MultiSearchData = Blog | CaseStudy | News | Page | Author;

const cropLength = 30;

const buildBreadcrumbs = (arr: string[]) =>
  [getHomeBreadcrumb().slug, ...arr].map((slug) => ({ slug }));

/** Meilisearch index uids */
const ids = {
  blog: "blog",
  caseStudy: "case-studie",
  news: "news-item",
  page: "page",
  author: "author",
};

type MultiSearchConfig<TData> = {
  buildQuery: (q: string) => MultiSearchQueryWithFederation;
  buildHitData: (hit: Hit<TData>) => HitData | undefined;
};

const blogConfig = (): MultiSearchConfig<Blog> => {
  type Data = Blog;
  const indexUid = ids.blog;

  return {
    buildQuery: (q) => ({
      indexUid,
      q,
      attributesToHighlight: ["title", "content"] satisfies (keyof Data)[],
      attributesToCrop: ["content"] satisfies (keyof Data)[],
      sort: ["publicationTimestamp:desc"],
      cropLength,
    }),
    buildHitData: (hit) =>
      hit?.categorySlug
        ? {
            id: `${hit.id} (${indexUid})`,
            title: hit._formatted?.title || hit.title,
            content: hit._formatted?.content || hit.content || "",
            link: routes.blog(hit.categorySlug, hit.slug),
            breadcrumbs: buildBreadcrumbs(["Blogs", hit.categorySlug]),
          }
        : undefined,
  };
};

const newsConfig = (): MultiSearchConfig<News> => {
  type Data = News;
  const indexUid = ids.news;

  return {
    buildQuery: (q) => ({
      indexUid,
      q,
      attributesToHighlight: ["title", "content"] satisfies (keyof Data)[],
      attributesToCrop: ["content"] satisfies (keyof Data)[],
      sort: ["publicationTimestamp:desc"],
      cropLength,
    }),
    buildHitData: (hit) =>
      hit.slug
        ? {
            id: `${hit.id} (${indexUid})`,
            title: hit._formatted?.title || hit.title,
            content: hit._formatted?.content || hit.content || "",
            link: routes.singleCompanyNews(hit.slug),
            breadcrumbs: buildBreadcrumbs(["Company News", hit.slug]),
          }
        : undefined,
  };
};

const pageConfig = (): MultiSearchConfig<Page> => {
  type Data = Page;
  const indexUid = ids.page;

  const mapping: Record<
    Page["strapiId"],
    {
      getLink: (slug: string) => string;
      getBreadcrumbs: (slug: string) => Breadcrumb[];
    }
  > = {
    page: {
      getLink: (slug) => routes.page(slug),
      getBreadcrumbs: (slug) => buildBreadcrumbs([slug]),
    },
    "business-sector": {
      getLink: (slug) => routes.dynamicPage("sectors", slug),
      getBreadcrumbs: (slug) => buildBreadcrumbs(["sectors", slug]),
    },
    expertise: {
      getLink: (slug) => routes.dynamicPage("expertise", slug),
      getBreadcrumbs: (slug) => buildBreadcrumbs(["expertise", slug]),
    },
    capability: {
      getLink: (slug) => routes.dynamicPage("capabilities", slug),
      getBreadcrumbs: (slug) => buildBreadcrumbs(["capabilities", slug]),
    },
    service: {
      getLink: (slug) => routes.dynamicPage("services", slug),
      getBreadcrumbs: (slug) => buildBreadcrumbs(["services", slug]),
    },
    solution: {
      getLink: (slug) => routes.dynamicPage("solutions", slug),
      getBreadcrumbs: (slug) => buildBreadcrumbs(["solutions", slug]),
    },
  };

  return {
    buildQuery: (q) => ({
      indexUid,
      q,
      attributesToHighlight: ["title", "excerpt"] satisfies (keyof Data)[],
      attributesToCrop: ["excerpt"] satisfies (keyof Data)[],
      cropLength,
    }),
    buildHitData: (hit) => {
      const { getBreadcrumbs, getLink } = mapping[hit.strapiId];
      const link = getLink(hit.slug);
      const breadcrumbs = getBreadcrumbs(hit.slug);

      return hit.slug && link && breadcrumbs
        ? {
            id: `${hit.id} (${hit.strapiId})`,
            title: hit._formatted?.title || hit.title,
            content: hit._formatted?.excerpt || hit.excerpt || "",
            link,
            breadcrumbs,
          }
        : undefined;
    },
  };
};

const caseStudyConfig = (): MultiSearchConfig<CaseStudy> => {
  type Data = CaseStudy;
  const indexUid = ids.caseStudy;

  return {
    buildQuery: (q) => ({
      indexUid,
      q,
      attributesToHighlight: [
        "title",
        "content",
        "firstParagraph",
      ] satisfies (keyof Data)[],
      attributesToCrop: ["content", "firstParagraph"] satisfies (keyof Data)[],
      sort: ["publicationTimestamp:desc"],
      cropLength,
    }),
    buildHitData: (hit) => ({
      id: `${hit.id} (${indexUid})`,
      title: hit._formatted?.title || hit.title,
      content:
        hit._formatted?.content ||
        hit._formatted?.firstParagraph ||
        hit.content ||
        "",
      link: routes.successStory(hit.slug),
      breadcrumbs: buildBreadcrumbs(["Success Stories", hit.slug]),
    }),
  };
};

const authorConfig = (): MultiSearchConfig<Author> => {
  type Data = Author;
  const indexUid = ids.author;

  return {
    buildQuery: (q) => ({
      indexUid,
      q,
      attributesToHighlight: ["description"] satisfies (keyof Data)[],
      attributesToCrop: ["description"] satisfies (keyof Data)[],
      // Note: couldn't sort by both firstName and the lastName. It gives an error
      // response like: cannot compare a relevancy rule with a sort rule 🤷‍♂️.
      sort: ["lastName:desc"],
      cropLength,
    }),
    buildHitData: (hit) => ({
      id: `${hit.id} (${indexUid})`,
      title: `${hit.firstName} ${hit.lastName}`,
      content: hit._formatted?.description || hit.description || "",
      link: routes.author(hit.slug),
      breadcrumbs: buildBreadcrumbs(["Authors", hit.slug]),
    }),
  };
};

export const configs = {
  [ids.blog]: blogConfig(),
  [ids.caseStudy]: caseStudyConfig(),
  [ids.news]: newsConfig(),
  [ids.page]: pageConfig(),
  [ids.author]: authorConfig(),
} as const;
